import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { EditorConfigModal } from "@/components/admin/editor-config-modal";
import { Pagination } from "@/components/ui/pagination";
import { useAdminCountries } from "@/hooks/use-admin-countries";

/**
 * Admin → Users.
 *
 * Port of src/app/admin/users/page.tsx (legacy NextJS) per D21 NO REINVENTION.
 *
 * Optimizations declared (Architecture Discipline Rule 1 + D4):
 *   - raw fetch() → apiFetch.get
 *   - useTranslations next-intl → Lingui Trans/t macros
 *   - useState/useEffect for fetch → useQuery (shared cache, auto-refetch)
 *
 * Endpoints consumed (already live in apps/api):
 *   - GET /api/admin/users?country=&userType=&status=&search=&limit=&stats=true
 *   - GET /api/admin/users/:userId/editor-config
 *   - POST /api/admin/users/:userId/editor-config
 *   - DELETE /api/admin/users/:userId/editor-config
 */

interface UserProfile {
  firstName: string;
  lastName: string;
  photoUrl: string | null;
}

interface AdminEditorInfo {
  isActive: boolean;
  countryCodes: string[];
  sections: string[];
}

interface AdminUserRow {
  id: string;
  email: string | null;
  phone: string | null;
  userType: string;
  isSuperadmin: boolean;
  hasPremium: boolean;
  premiumSource: string | null;
  status: string;
  preferredLanguage: string | null;
  lastLoginAt: string | null;
  loginCount: number;
  createdAt: string;
  providerId: string | null;
  profileId: string | null;
  provider: UserProfile | null;
  profile: UserProfile | null;
  adminEditor: AdminEditorInfo | null;
}

interface UsersStats {
  total: number;
  active30d: number;
  premium: number;
  newThisMonth: number;
  byType: Record<string, number>;
}

type UserTypeFilter =
  | "all"
  | "user"
  | "patient"
  | "provider"
  | "facility_admin"
  | "admin_editor"
  | "superadmin";

type StatusFilter = "all" | "active" | "suspended" | "deleted" | "merged";

const USER_TYPES: UserTypeFilter[] = [
  "all",
  "user",
  "patient",
  "provider",
  "facility_admin",
  "superadmin",
  "admin_editor",
];

const STATUSES: StatusFilter[] = ["all", "active", "suspended", "deleted", "merged"];

const typeBadgeClass: Record<string, string> = {
  user: "bg-zinc-100 text-zinc-600",
  patient: "bg-blue-100 text-blue-700",
  provider: "bg-emerald-100 text-emerald-700",
  facility_admin: "bg-purple-100 text-purple-700",
  admin_editor: "bg-amber-100 text-amber-700",
  superadmin: "bg-red-100 text-red-700",
};

const statusBadgeClass: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
  deleted: "bg-zinc-100 text-zinc-500",
  merged: "bg-indigo-100 text-indigo-700",
};

function getUserDisplayName(user: AdminUserRow): string | null {
  const p = user.provider || user.profile;
  return p ? `${p.firstName} ${p.lastName}` : null;
}

function getUserPhoto(user: AdminUserRow): string | null {
  return user.provider?.photoUrl || user.profile?.photoUrl || null;
}

function getUserInitial(user: AdminUserRow): string {
  const p = user.provider || user.profile;
  return (p?.firstName?.[0] || user.email?.[0] || "?").toUpperCase();
}

export const Route = createFileRoute("/_authenticated/users")({
  component: UsersPage,
});

function UsersPage() {
  const { t } = useLingui();
  const { visibleCountries: countries } = useAdminCountries();

  const [selectedCountry, setSelectedCountry] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editorModalUserId, setEditorModalUserId] = useState("");
  const [editorModalEmail, setEditorModalEmail] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of countries) {
      if (c.totalProviders > 0) map[c.code] = c.totalProviders;
    }
    return map;
  }, [countries]);

  const buildQS = (extra: Record<string, string | undefined>): string => {
    const sp = new URLSearchParams();
    if (selectedCountry) sp.set("country", selectedCountry);
    if (debouncedSearch) sp.set("search", debouncedSearch);
    for (const [k, v] of Object.entries(extra)) {
      if (v) sp.set(k, v);
    }
    return sp.toString();
  };

  // Reset pagination when filters change.
  useEffect(() => {
    setOffset(0);
  }, [selectedCountry, userTypeFilter, statusFilter, debouncedSearch]);

  const usersQuery = useQuery({
    queryKey: [
      "admin",
      "users",
      "list",
      selectedCountry,
      userTypeFilter,
      statusFilter,
      debouncedSearch,
      offset,
    ],
    queryFn: async (): Promise<{ users: AdminUserRow[]; total: number }> => {
      try {
        const data = await apiFetch.get<{ users: AdminUserRow[]; total: number }>(
          `/api/admin/users?${buildQS({
            userType: userTypeFilter !== "all" ? userTypeFilter : undefined,
            status: statusFilter !== "all" ? statusFilter : undefined,
            limit: String(PAGE_SIZE),
            offset: String(offset),
          })}`,
        );
        return { users: data.users || [], total: data.total || 0 };
      } catch (error) {
        if (error instanceof ApiError) return { users: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "users", "stats", selectedCountry, debouncedSearch],
    queryFn: async (): Promise<UsersStats | null> => {
      try {
        const data = await apiFetch.get<{ stats: UsersStats }>(
          `/api/admin/users?${buildQS({ stats: "true" })}`,
        );
        return data.stats;
      } catch (error) {
        if (error instanceof ApiError) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const users = usersQuery.data?.users ?? [];
  const total = usersQuery.data?.total ?? 0;
  const stats = statsQuery.data ?? null;

  const refreshAll = () => {
    void usersQuery.refetch();
    void statsQuery.refetch();
  };

  const openEditorModal = (user: AdminUserRow) => {
    setEditorModalUserId(user.id);
    setEditorModalEmail(user.email || user.phone || "");
    setEditorModalOpen(true);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatRelative = (dateStr: string | null) => {
    if (!dateStr) return "—";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return t`today`;
    if (days === 1) return t`yesterday`;
    if (days < 30) return t`${days} days ago`;
    const months = Math.floor(days / 30);
    return t`${months} months ago`;
  };

  const userTypeLabel = (type: UserTypeFilter): string => {
    switch (type) {
      case "all":
        return t`All types`;
      case "user":
        return t`User`;
      case "patient":
        return t`Patient`;
      case "provider":
        return t`Provider`;
      case "facility_admin":
        return t`Facility admin`;
      case "admin_editor":
        return t`Admin editor`;
      case "superadmin":
        return t`Superadmin`;
    }
  };

  const userTypeShort = (type: string): string => {
    return userTypeLabel(type as UserTypeFilter);
  };

  const statusLabel = (status: StatusFilter): string => {
    switch (status) {
      case "all":
        return t`All statuses`;
      case "active":
        return t`Active`;
      case "suspended":
        return t`Suspended`;
      case "deleted":
        return t`Deleted`;
      case "merged":
        return t`Merged`;
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Users</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Patient + staff accounts. Search, filter, grant editor permissions.</Trans>
        </p>
      </header>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={setSelectedCountry}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: <Trans>Total</Trans>,
            value: stats?.total ?? "—",
            icon: "👤",
            color: "text-blue-600",
          },
          {
            label: <Trans>Active 30d</Trans>,
            value: stats?.active30d ?? "—",
            icon: "✅",
            color: "text-emerald-600",
          },
          {
            label: <Trans>Premium</Trans>,
            value: stats?.premium ?? "—",
            icon: "⭐",
            color: "text-purple-600",
          },
          {
            label: <Trans>New this month</Trans>,
            value: stats?.newThisMonth ?? "—",
            icon: "🆕",
            color: "text-amber-600",
          },
        ].map((stat, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <div className={`mt-2 text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-muted-foreground text-xs">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stats?.byType && Object.keys(stats.byType).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.byType)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => (
              <button
                key={type}
                onClick={() => setUserTypeFilter(type as UserTypeFilter)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all ${
                  userTypeFilter === type ? "ring-primary ring-2 ring-offset-1" : "hover:opacity-80"
                } ${typeBadgeClass[type] || "bg-zinc-100 text-zinc-600"}`}
              >
                {userTypeShort(type)}
                <span className="opacity-60">({count})</span>
              </button>
            ))}
          {userTypeFilter !== "all" && (
            <button
              onClick={() => setUserTypeFilter("all")}
              className="text-muted-foreground hover:text-foreground rounded-full px-3 py-1 text-xs underline transition-colors"
            >
              <Trans>Clear filter</Trans>
            </button>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex-1">
              <Input
                placeholder={t`Search by name or email…`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
              />
            </div>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value as UserTypeFilter)}
              className="border-border bg-background text-foreground h-9 rounded-md border px-3 text-sm"
            >
              {USER_TYPES.map((value) => (
                <option key={value} value={value}>
                  {userTypeLabel(value)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="border-border bg-background text-foreground h-9 rounded-md border px-3 text-sm"
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value)}
                </option>
              ))}
            </select>
            <span className="text-muted-foreground text-xs whitespace-nowrap">
              <Trans>{total} results</Trans>
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            <Trans>User accounts</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {usersQuery.isLoading ? (
            <div className="space-y-0 divide-y">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <div className="bg-muted h-8 w-8 animate-pulse rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="bg-muted h-3.5 w-32 animate-pulse rounded" />
                    <div className="bg-muted h-3 w-48 animate-pulse rounded" />
                  </div>
                  <div className="bg-muted hidden h-5 w-16 animate-pulse rounded-full sm:block" />
                  <div className="bg-muted hidden h-5 w-14 animate-pulse rounded-full md:block" />
                  <div className="bg-muted hidden h-3 w-20 animate-pulse rounded lg:block" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="border-border/50 m-4 rounded-lg border border-dashed p-8 text-center">
              <span className="text-4xl">👤</span>
              <p className="text-muted-foreground mt-3 text-sm">
                <Trans>No users match these filters.</Trans>
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">
                      <Trans>User</Trans>
                    </TableHead>
                    <TableHead className="w-[120px]">
                      <Trans>Type</Trans>
                    </TableHead>
                    <TableHead className="w-[120px]">
                      <Trans>Role</Trans>
                    </TableHead>
                    <TableHead className="hidden w-[100px] sm:table-cell">
                      <Trans>Status</Trans>
                    </TableHead>
                    <TableHead className="hidden w-[100px] lg:table-cell">
                      <Trans>Last login</Trans>
                    </TableHead>
                    <TableHead className="hidden w-[100px] xl:table-cell">
                      <Trans>Created</Trans>
                    </TableHead>
                    <TableHead className="w-[90px]">
                      <Trans>Actions</Trans>
                    </TableHead>
                    <TableHead className="w-8 md:hidden" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <ExpandableRow
                      key={user.id}
                      mobileDetails={[
                        {
                          label: <Trans>Role</Trans>,
                          value: <RoleBadges user={user} />,
                        },
                        {
                          label: <Trans>Status</Trans>,
                          value: (
                            <Badge
                              variant="secondary"
                              className={`text-[0.625rem] ${statusBadgeClass[user.status] || ""}`}
                            >
                              {statusLabel(user.status as StatusFilter)}
                            </Badge>
                          ),
                        },
                        {
                          label: <Trans>Last login</Trans>,
                          value: formatRelative(user.lastLoginAt),
                        },
                        {
                          label: <Trans>Created</Trans>,
                          value: formatDate(user.createdAt),
                        },
                        {
                          label: <Trans>Logins</Trans>,
                          value: user.loginCount.toString(),
                        },
                      ]}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium">
                            {getUserPhoto(user) ? (
                              <img
                                src={getUserPhoto(user)!}
                                alt=""
                                className="h-8 w-8 rounded-full object-cover"
                              />
                            ) : (
                              getUserInitial(user)
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                              {getUserDisplayName(user) || <Trans>Unnamed</Trans>}
                            </div>
                            <div className="text-muted-foreground truncate text-xs">
                              {user.email || user.phone || "—"}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={`text-[0.625rem] ${typeBadgeClass[user.userType] || ""}`}
                        >
                          {userTypeShort(user.userType)}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <RoleBadges user={user} />
                      </TableCell>

                      <TableCell className="hidden sm:table-cell">
                        <Badge
                          variant="secondary"
                          className={`text-[0.625rem] ${statusBadgeClass[user.status] || ""}`}
                        >
                          {statusLabel(user.status as StatusFilter)}
                        </Badge>
                      </TableCell>

                      <TableCell className="hidden lg:table-cell">
                        <span className="text-muted-foreground text-xs">
                          {formatRelative(user.lastLoginAt)}
                        </span>
                      </TableCell>

                      <TableCell className="hidden xl:table-cell">
                        <span className="text-muted-foreground text-xs">
                          {formatDate(user.createdAt)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[0.6875rem]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditorModal(user);
                          }}
                        >
                          {user.adminEditor?.isActive ? (
                            <Trans>Edit perms</Trans>
                          ) : (
                            <Trans>Make editor</Trans>
                          )}
                        </Button>
                      </TableCell>
                    </ExpandableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {!usersQuery.isLoading && users.length > 0 && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={total}
          onChange={(next) => setOffset(next)}
        />
      )}

      <EditorConfigModal
        open={editorModalOpen}
        onOpenChange={setEditorModalOpen}
        userId={editorModalUserId}
        userEmail={editorModalEmail}
        countries={countries}
        onSaved={refreshAll}
      />
    </div>
  );
}

function RoleBadges({ user }: { user: AdminUserRow }) {
  const isEditor = user.adminEditor?.isActive;
  return (
    <div className="flex flex-wrap gap-1">
      {user.isSuperadmin && (
        <Badge variant="secondary" className="bg-red-100 text-[0.625rem] font-bold text-red-700">
          SA
        </Badge>
      )}
      {isEditor && (
        <Badge
          variant="secondary"
          className="bg-amber-100 text-[0.625rem] font-bold text-amber-700"
        >
          <Trans>Editor</Trans>
        </Badge>
      )}
      {!user.isSuperadmin && !isEditor && <span className="text-muted-foreground text-xs">—</span>}
    </div>
  );
}
